<template>
    <div class="growth-velocity-chart">
        <div class="chart-header">
            <h4>
                <i class="bi bi-graph-up-arrow"></i>
                Growth Tracking
            </h4>
            <div class="chart-controls" v-if="measurements.length > 0">
                <select v-model="chartType" class="form-select form-select-sm">
                    <option value="height">Height Over Time</option>
                    <option value="velocity">Growth Velocity</option>
                    <option value="pah">% Predicted Adult Height</option>
                </select>
            </div>
        </div>

        <!-- Loading State -->
        <div v-if="loading" class="loading-state">
            <div class="spinner-border spinner-border-sm" role="status">
                <span class="visually-hidden">Loading...</span>
            </div>
            <span>Loading measurement history...</span>
        </div>

        <!-- No Data State -->
        <div v-else-if="measurements.length === 0" class="no-data-state">
            <i class="bi bi-clipboard-data"></i>
            <p>No measurement history available</p>
            <small>Save bio-banding assessments to track growth over time</small>
        </div>

        <!-- Chart -->
        <div v-else class="chart-container">
            <canvas ref="chartCanvas"></canvas>
        </div>

        <!-- Stats Summary -->
        <div v-if="measurements.length > 1" class="stats-summary">
            <div class="stat-item">
                <label>Measurements</label>
                <span>{{ measurements.length }}</span>
            </div>
            <div class="stat-item">
                <label>First Recorded</label>
                <span>{{ formatDate(measurements[measurements.length - 1]?.measurement_date) }}</span>
            </div>
            <div class="stat-item">
                <label>Latest</label>
                <span>{{ formatDate(measurements[0]?.measurement_date) }}</span>
            </div>
            <div class="stat-item" v-if="latestVelocity">
                <label>Current Growth Rate</label>
                <span :class="{ 'text-danger': latestVelocity > 6, 'fw-bold': latestVelocity > 6 }">
                    {{ latestVelocity.toFixed(1) }} cm/yr
                    <i v-if="latestVelocity > 6" class="bi bi-exclamation-triangle text-danger"></i>
                </span>
            </div>
            <div class="stat-item" v-if="totalGrowth">
                <label>Total Growth</label>
                <span>{{ totalGrowth.toFixed(1) }} cm</span>
            </div>
        </div>

        <!-- Risk Alert -->
        <div v-if="latestVelocity && latestVelocity > 6" class="risk-alert">
            <i class="bi bi-exclamation-triangle-fill"></i>
            <div>
                <strong>Elevated Growth Rate Detected</strong>
                <p>Growth rate exceeds 6cm/year threshold. This player may be at increased risk of growth-related
                    injuries. Consider reducing training loads and increasing monitoring.</p>
            </div>
        </div>

        <!-- Measurement History Table -->
        <div v-if="showHistory && measurements.length > 0" class="history-section">
            <div class="history-header">
                <h5>Measurement History</h5>
                <button class="btn btn-sm btn-outline-secondary" @click="showHistoryTable = !showHistoryTable">
                    {{ showHistoryTable ? 'Hide' : 'Show' }} Table
                </button>
            </div>

            <table v-if="showHistoryTable" class="table table-sm">
                <thead>
                    <tr>
                        <th>Date</th>
                        <th>Height</th>
                        <th>Weight</th>
                        <th>%PAH</th>
                        <th>Bio Age</th>
                        <th>Growth Rate</th>
                        <th>Status</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="m in measurements" :key="m.id"
                        :class="{ 'table-warning': parseFloat(m.growthVelocity) > 6 }">
                        <td>{{ formatDate(m.measurement_date) }}</td>
                        <td>{{ m.childsHeight ? m.childsHeight + ' cm' : '-' }}</td>
                        <td>{{ m.childsWeight ? m.childsWeight + ' kg' : '-' }}</td>
                        <td>{{ formatNumber(m.percentPAH) ? formatNumber(m.percentPAH) + '%' : '-' }}</td>
                        <td>{{ formatNumber(m.bioAge) || '-' }}</td>
                        <td :class="{ 'text-danger fw-bold': parseFloat(m.growthVelocity) > 6 }">
                            {{ formatNumber(m.growthVelocity) ? formatNumber(m.growthVelocity) + ' cm/yr' : '-' }}
                        </td>
                        <td>
                            <span class="badge" :class="getMaturityBadgeClass(m.maturityCategory)">
                                {{ m.maturityCategory || 'N/A' }}
                            </span>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>
</template>

<script>
import { Chart, registerables } from 'chart.js';
import { PlayerPhysicalPerformance } from "@/services/player-physical-performance-service.js";

Chart.register(...registerables);

export default {
    name: "GrowthVelocityChart",
    props: {
        userId: {
            type: [Number, String],
            required: true
        },
        showHistory: {
            type: Boolean,
            default: true
        }
    },
    data() {
        return {
            loading: true,
            measurements: [],
            chartType: 'height',
            showHistoryTable: false,
            chart: null
        };
    },
    computed: {
        latestVelocity() {
            const velocity = this.measurements[0]?.growthVelocity;
            return velocity != null ? parseFloat(velocity) : null;
        },
        totalGrowth() {
            if (this.measurements.length < 2) return null;
            const latest = parseFloat(this.measurements[0]?.childsHeight);
            const earliest = parseFloat(this.measurements[this.measurements.length - 1]?.childsHeight);
            if (!isNaN(latest) && !isNaN(earliest)) {
                return latest - earliest;
            }
            return null;
        }
    },
    watch: {
        userId: {
            handler() {
                this.loadHistory();
            },
            immediate: true
        },
        chartType() {
            this.updateChart();
        }
    },
    beforeUnmount() {
        if (this.chart) {
            this.chart.destroy();
        }
    },
    methods: {
        async loadHistory() {
            this.loading = true;
            try {
                const response = await PlayerPhysicalPerformance.getBioAgeHistory(this.userId);
                this.measurements = response.data || [];
                this.$nextTick(() => {
                    if (this.measurements.length > 0) {
                        this.initChart();
                    }
                });
            } catch (err) {
                console.error('Error loading bio age history:', err);
                this.measurements = [];
            } finally {
                this.loading = false;
            }
        },

        initChart() {
            if (this.chart) {
                this.chart.destroy();
            }

            const canvas = this.$refs.chartCanvas;
            if (!canvas) return;

            const ctx = canvas.getContext('2d');
            this.chart = new Chart(ctx, this.getChartConfig());
        },

        updateChart() {
            if (this.chart) {
                this.chart.destroy();
            }
            this.initChart();
        },

        getChartConfig() {
            // Reverse to get chronological order (oldest first)
            const sortedData = [...this.measurements].reverse();
            const labels = sortedData.map(m => this.formatDateShort(m.measurement_date));

            let datasets = [];
            let yAxisLabel = '';
            let suggestedMin = null;
            let suggestedMax = null;

            switch (this.chartType) {
                case 'height':
                    datasets = [{
                        label: 'Height (cm)',
                        data: sortedData.map(m => m.childsHeight),
                        borderColor: '#2196f3',
                        backgroundColor: 'rgba(33, 150, 243, 0.1)',
                        fill: true,
                        tension: 0.3,
                        pointRadius: 4,
                        pointHoverRadius: 6
                    }];
                    yAxisLabel = 'Height (cm)';
                    break;

                case 'velocity':
                    datasets = [
                        {
                            label: 'Growth Velocity (cm/year)',
                            data: sortedData.map(m => m.growthVelocity),
                            borderColor: '#4caf50',
                            backgroundColor: 'rgba(76, 175, 80, 0.1)',
                            fill: true,
                            tension: 0.3,
                            pointRadius: 4,
                            pointHoverRadius: 6
                        },
                        {
                            label: 'Risk Threshold (6 cm/yr)',
                            data: sortedData.map(() => 6),
                            borderColor: '#f44336',
                            borderDash: [5, 5],
                            fill: false,
                            pointRadius: 0,
                            borderWidth: 2
                        }
                    ];
                    yAxisLabel = 'Growth Rate (cm/year)';
                    suggestedMin = 0;
                    suggestedMax = 12;
                    break;

                case 'pah':
                    datasets = [
                        {
                            label: '% Predicted Adult Height',
                            data: sortedData.map(m => m.percentPAH),
                            borderColor: '#9c27b0',
                            backgroundColor: 'rgba(156, 39, 176, 0.1)',
                            fill: true,
                            tension: 0.3,
                            pointRadius: 4,
                            pointHoverRadius: 6
                        },
                        {
                            label: 'PHV Zone Start (85%)',
                            data: sortedData.map(() => 85),
                            borderColor: '#ff9800',
                            borderDash: [3, 3],
                            fill: false,
                            pointRadius: 0,
                            borderWidth: 1
                        },
                        {
                            label: 'PHV Zone End (95%)',
                            data: sortedData.map(() => 95),
                            borderColor: '#ff9800',
                            borderDash: [3, 3],
                            fill: false,
                            pointRadius: 0,
                            borderWidth: 1
                        }
                    ];
                    yAxisLabel = '% PAH';
                    suggestedMin = 70;
                    suggestedMax = 100;
                    break;
            }

            return {
                type: 'line',
                data: {
                    labels,
                    datasets
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                        legend: {
                            position: 'bottom',
                            labels: {
                                usePointStyle: true,
                                padding: 16
                            }
                        },
                        tooltip: {
                            backgroundColor: 'rgba(0, 0, 0, 0.8)',
                            padding: 12,
                            titleFont: { size: 14, weight: 'bold' },
                            bodyFont: { size: 13 },
                            callbacks: {
                                afterBody: (context) => {
                                    const index = context[0].dataIndex;
                                    const measurement = sortedData[index];
                                    const lines = [];

                                    if (measurement.maturityCategory) {
                                        lines.push(`Status: ${measurement.maturityCategory}`);
                                    }
                                    if (measurement.bioAge) {
                                        lines.push(`Bio Age: ${this.formatNumber(measurement.bioAge)}`);
                                    }
                                    if (this.chartType !== 'velocity' && measurement.growthVelocity) {
                                        lines.push(`Growth: ${this.formatNumber(measurement.growthVelocity)} cm/yr`);
                                    }

                                    return lines.length ? ['', ...lines] : [];
                                }
                            }
                        }
                    },
                    scales: {
                        x: {
                            grid: {
                                display: false
                            },
                            title: {
                                display: true,
                                text: 'Measurement Date'
                            }
                        },
                        y: {
                            title: {
                                display: true,
                                text: yAxisLabel
                            },
                            suggestedMin,
                            suggestedMax,
                            grid: {
                                color: 'rgba(0, 0, 0, 0.05)'
                            }
                        }
                    },
                    interaction: {
                        intersect: false,
                        mode: 'index'
                    }
                }
            };
        },

        formatDate(dateStr) {
            if (!dateStr) return '-';
            const date = new Date(dateStr);
            return date.toLocaleDateString('en-GB', {
                day: 'numeric',
                month: 'short',
                year: 'numeric'
            });
        },

        formatDateShort(dateStr) {
            if (!dateStr) return '-';
            const date = new Date(dateStr);
            return date.toLocaleDateString('en-GB', {
                day: 'numeric',
                month: 'short',
                year: '2-digit'
            });
        },

        getMaturityBadgeClass(category) {
            switch (category) {
                case 'Pre-PHV': return 'bg-info';
                case 'Circa-PHV': return 'bg-warning text-dark';
                case 'Post-PHV': return 'bg-success';
                default: return 'bg-secondary';
            }
        },

        formatNumber(value, decimals = 1) {
            if (value === null || value === undefined || value === '') return null;
            const num = parseFloat(value);
            return isNaN(num) ? null : num.toFixed(decimals);
        }
    }
};
</script>

<style scoped>
.growth-velocity-chart {
    background: white;
    border-radius: 12px;
    padding: 20px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.chart-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
}

.chart-header h4 {
    margin: 0;
    font-size: 16px;
    font-weight: 600;
    display: flex;
    align-items: center;
    gap: 8px;
}

.chart-header h4 i {
    color: #4caf50;
}

.chart-controls select {
    width: auto;
    min-width: 180px;
}

.loading-state {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 40px;
    color: #6c757d;
}

.no-data-state {
    text-align: center;
    padding: 40px 20px;
    color: #6c757d;
}

.no-data-state i {
    font-size: 48px;
    margin-bottom: 12px;
    opacity: 0.5;
}

.no-data-state p {
    margin: 0 0 4px;
    font-weight: 500;
}

.no-data-state small {
    opacity: 0.7;
}

.chart-container {
    height: 280px;
    position: relative;
}

.stats-summary {
    display: flex;
    flex-wrap: wrap;
    gap: 20px;
    margin-top: 20px;
    padding-top: 16px;
    border-top: 1px solid #e0e0e0;
}

.stat-item {
    display: flex;
    flex-direction: column;
    gap: 2px;
}

.stat-item label {
    font-size: 11px;
    color: #9e9e9e;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.stat-item span {
    font-size: 14px;
    font-weight: 600;
    color: #333;
}

.risk-alert {
    display: flex;
    gap: 12px;
    margin-top: 16px;
    padding: 12px 16px;
    background: #fff3e0;
    border-radius: 8px;
    border-left: 4px solid #f57c00;
}

.risk-alert i {
    font-size: 24px;
    color: #f57c00;
    flex-shrink: 0;
}

.risk-alert strong {
    display: block;
    color: #e65100;
    margin-bottom: 4px;
}

.risk-alert p {
    margin: 0;
    font-size: 13px;
    color: #5d4037;
    line-height: 1.5;
}

.history-section {
    margin-top: 20px;
    padding-top: 16px;
    border-top: 1px solid #e0e0e0;
}

.history-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
}

.history-header h5 {
    margin: 0;
    font-size: 14px;
    font-weight: 600;
}

.history-section .table {
    font-size: 13px;
}

.history-section .table th {
    font-weight: 600;
    background: #f8f9fa;
    white-space: nowrap;
}

.history-section .table td {
    vertical-align: middle;
}

@media (max-width: 576px) {
    .chart-header {
        flex-direction: column;
        align-items: flex-start;
        gap: 12px;
    }

    .chart-controls select {
        width: 100%;
    }

    .stats-summary {
        gap: 12px;
    }

    .stat-item {
        flex-basis: calc(50% - 6px);
    }
}
</style>
